import React, { useRef, useState } from "react";
import { Grid } from "@mui/material";

import { FormButton, SpinnerOverlay } from "components";
import {
  FormikDateInput,
  FormikSelectInput,
  FormikTextInput,
} from "components/UI/Inputs/FormikInputs";

import { Form, Formik } from "formik";
import * as yup from "yup";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";

import styles from "styles/pages/ModalActions.module.scss";
import { LARGO, mensajeLargo } from "consts/limites-campos";
import {
  fechaNacimientoMaxima,
  fechaNacimientoMinima,
  fechaNacimientoValida,
  MENSAJE_FECHA_NACIMIENTO,
} from "consts/fecha-nacimiento";

import { MIAConnectionIns, usuarioBasicosGet } from "services/services-mia";
import { GENEROS, TIPO_DOCUMENTO } from "../consts";

/**
 * Alta de un usuario interno, en dos pasos dentro del mismo modal.
 *
 *   BÚSQUEDA → documento + género + fecha de nacimiento, que son la clave con la que
 *              `usuarioBasicosGet` dice si la persona ya está en MIA.
 *   CARGA    → nombre, apellido, email y rol. Los tres primeros quedan de SOLO LECTURA
 *              si la persona ya existía: su identidad la administra MIA, no este panel.
 *              Si no existía, se escriben y se crea junto con la conexión.
 *
 * Es el mismo recorrido que el alta de agentes; lo que cambia es lo que se hace al
 * final: acá se crea la conexión con el servicio y su rol.
 */
const PASO = { BUSQUEDA: 1, CARGA: 2 };

export default function InsertModal({ roles, servicio, updateList, closeModal }) {
  const setSnackBarInfo = useSetRecoilState(snackbarData);
  const credentials = useRecoilValue(userCredentials);
  const formikRef = useRef();

  const [paso, setPaso] = useState(PASO.BUSQUEDA);
  // `null` hasta buscar. Define si los datos personales se editan o se muestran.
  const [personaExiste, setPersonaExiste] = useState(null);
  const [inRequest, setInRequest] = useState(false);

  const enBusqueda = paso === PASO.BUSQUEDA;
  // Lo que MIA ya sabe de la persona no se toca desde acá.
  const bloqueado = personaExiste === true;

  const buscarPersona = async (values) => {
    setInRequest(true);
    const respuesta = await usuarioBasicosGet(
      {
        usuarioNroDeDocumento: values.documento,
        usuarioSexo: values.genero,
        usuarioFechaDeNacimiento: values.fechaNacimiento,
      },
      credentials
    );
    setInRequest(false);

    const encontrados = respuesta?.result ?? [];

    // Más de uno con la misma clave es un dato inconsistente, no un caso a resolver
    // eligiendo: se corta acá en vez de dar de alta el equivocado.
    if (encontrados.length > 1) {
      setSnackBarInfo({
        message:
          "Hay más de una persona con esos datos. Verificá documento, género y fecha de nacimiento, o contactá a soporte.",
        severity: "warning",
        open: true,
      });
      return;
    }

    const persona = encontrados[0];
    setPersonaExiste(Boolean(persona));

    /**
     * `resetForm` y no tres `setFieldValue`, por el `submitCount`.
     *
     * "Continuar" es un submit, asi que al llegar al paso dos el formulario ya cuenta un
     * envio. `useErrorAlSubmit` —la regla de mostrar errores solo despues de enviar— lee ese
     * contador, y como `validateOnChange` sigue activo, la primera tecla encendia el error de
     * todos los campos requeridos del paso dos sin que nadie hubiera intentado guardar.
     *
     * `resetForm` lo pone en cero, y de paso limpia `touched` y los errores de la busqueda.
     * Los valores se pasan en el mismo llamado para no perder lo ya cargado.
     */
    formikRef.current.resetForm({
      values: {
        ...formikRef.current.values,
        nombres: persona?.usuarioNombres ?? "",
        apellidos: persona?.usuarioApellidos ?? "",
        email: persona?.usuarioMail ?? "",
      },
    });
    setPaso(PASO.CARGA);
  };

  const volverABuscar = () => {
    // Por lo mismo que arriba: volver al paso uno no tiene que arrastrar el "ya se envio"
    // del dos, o la clave de busqueda se abriria con los errores puestos.
    formikRef.current.resetForm({ values: formikRef.current.values });
    setPaso(PASO.BUSQUEDA);
    setPersonaExiste(null);
  };

  const crearUsuario = async (values) => {
    setInRequest(true);

    const respuesta = await MIAConnectionIns(
      {
        merchantId: credentials.merchantId,
        servicioCod: servicio?.servicioCod,
        rolCod: values.rol,
        tipoDeDocumentoCod: TIPO_DOCUMENTO,
        usuarioNroDeDocumentoX: values.documento,
        usuarioSexo: values.genero,
        usuarioFechaDeNacimiento: values.fechaNacimiento,
        usuarioMail: values.email,
        usuarioApellidos: values.apellidos,
        usuarioNombres: values.nombres,
        // "S" = el vínculo es con el SERVICIO.
        merchantServicioUsuarioTiporel: "S",
        // "Y" = la conexión se da por aceptada, sin esperar que la persona la confirme
        // desde la app. Es lo que corresponde para un usuario interno: el acceso al panel
        // se lo da el organismo, no es una invitación que el usuario elige tomar.
        merchantServicioUsuarioForzado: "Y",
      },
      credentials
    );
    setInRequest(false);

    if (respuesta?.status?.code !== 1) {
      setSnackBarInfo({
        message: respuesta?.status?.errmsg || "No se pudo crear el usuario",
        severity: "error",
        open: true,
      });
      return;
    }

    setSnackBarInfo({
      message: "Usuario creado correctamente",
      severity: "success",
      open: true,
    });
    formikRef.current.resetForm();
    updateList();
    closeModal();
  };

  /**
   * Las reglas del segundo paso solo se aplican cuando se llegó a él.
   *
   * Con `enBusqueda` el submit valida únicamente la clave de búsqueda; si no, los
   * campos del paso dos —que todavía están vacíos— impedirían buscar.
   */
  const validationSchema = yup.object({
    documento: yup
      .string()
      .matches(/^\d+$/, "Solo números, sin puntos")
      .required("Debe ingresar el documento"),
    genero: yup.string().required("Debe seleccionar un género"),
    fechaNacimiento: yup
      .string()
      .required("Debe ingresar la fecha de nacimiento")
      // El calendario ya acota el rango; esto cubre lo tipeado o pegado.
      .test("rango", MENSAJE_FECHA_NACIMIENTO, fechaNacimientoValida),
    ...(enBusqueda
      ? {}
      : {
        nombres: yup
          .string()
          .max(LARGO.nombre, mensajeLargo(LARGO.nombre))
          .required("Debe ingresar el nombre"),
        apellidos: yup
          .string()
          .max(LARGO.nombre, mensajeLargo(LARGO.nombre))
          .required("Debe ingresar el apellido"),
        email: yup
          .string()
          .email("Debe ingresar un email válido")
          .required("Debe ingresar el email"),
        rol: yup.string().required("Debe seleccionar un rol"),
      }),
  });

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        documento: "",
        genero: "",
        fechaNacimiento: "",
        nombres: "",
        apellidos: "",
        email: "",
        rol: "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values) =>
        enBusqueda ? buscarPersona(values) : crearUsuario(values)
      }
    >
      <Form className={styles.inputs} noValidate>
        <div className={styles["column"]} style={{ width: "100%" }}>
          <Grid
            container
            rowSpacing={0}
            columnSpacing={2}
            sx={{
              width: "100%",
              "& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
            }}
          >
            {/* La clave de búsqueda. Una vez encontrada la persona queda
                            fija: cambiarla sería buscar a otra. */}
            <Grid item xs={6}>
              <FormikTextInput
                name="documento"
                labelText="Documento"
                placeholder="Sin puntos"
                disabled={!enBusqueda}
                inputProps={{ maxLength: 8, inputMode: "numeric" }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormikSelectInput
                name="genero"
                labelText="Género"
                disabled={!enBusqueda}
              >
                <option value="" disabled>
                  Seleccione
                </option>
                {GENEROS.map(({ cod, label }) => (
                  <option key={cod} value={cod}>
                    {label}
                  </option>
                ))}
              </FormikSelectInput>
            </Grid>
            <Grid item xs={12}>
              <FormikDateInput
                name="fechaNacimiento"
                labelText="Fecha de nacimiento"
                disabled={!enBusqueda}
                inputProps={{
                  min: fechaNacimientoMinima(),
                  max: fechaNacimientoMaxima(),
                }}
              />
            </Grid>

            {!enBusqueda && (
              <>
                <Grid item xs={6}>
                  <FormikTextInput
                    name="apellidos"
                    labelText="Apellidos"
                    disabled={bloqueado}
                    inputProps={{ maxLength: LARGO.nombre }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormikTextInput
                    name="nombres"
                    labelText="Nombres"
                    disabled={bloqueado}
                    inputProps={{ maxLength: LARGO.nombre }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormikTextInput
                    name="email"
                    labelText="Email"
                    placeholder="ejemplo@mail.com"
                    // Editable aunque la persona exista: puede estar
                    // en MIA sin mail cargado.
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormikSelectInput name="rol" labelText="Rol">
                    <option value="" disabled>
                      Seleccione
                    </option>
                    {roles.map(({ rolCod, rolNombre }) => (
                      <option key={rolCod} value={rolCod}>
                        {rolNombre}
                      </option>
                    ))}
                  </FormikSelectInput>
                </Grid>
              </>
            )}
          </Grid>

          <FormButton
            inRequest={inRequest}
            newFormData={{ button: enBusqueda ? "Continuar" : "Crear" }}
            closeButtonText={enBusqueda ? "Cancelar" : "Volver"}
            onSecondaryClick={enBusqueda ? undefined : volverABuscar}
            onCloseModal={closeModal}
          />
          {inRequest && <SpinnerOverlay />}
        </div>
      </Form>
    </Formik>
  );
}
